import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import axiosInstance from '../services/axiosInstance';
import { toYmd, ymdToDate } from '../utils/dateOnly';
import { handleApiSuccess, handleApiError } from '../components/apiErrorHandler';
import RecordPaymentDialog from './RecordPaymentDialog';
import { printPOBarcodes } from '../printing/PrinterService';
import PrinterModeConfirmDialog from '../printing/PrinterModeConfirmDialog';
import PrintPOBarcodeDialog from './PrintPOBarcodeDialog';
import { printPurchaseOrder } from './PurchaseOrderPrintView';

export default function MoveOrderDetailDialog({ orderId, visible, onHide, onReinitiate, onChanged, canManageMoveOrders, canRecordPayments}) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderTotalCost, setOrderTotalCost] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendDialogVisible, setSendDialogVisible] = useState(false);
  const [sendEmailTo, setSendEmailTo] = useState('');
  const [receiveDialogVisible, setReceiveDialogVisible] = useState(false);
  const [receiveOnCredit, setReceiveOnCredit] = useState(false);
  const [receiveInvoiceNo, setReceiveInvoiceNo] = useState('');
  const [receiveItemsState, setReceiveItemsState] = useState([]); // local editable copy of items
  const [receiveSupplierId, setReceiveSupplierId] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [paymentDialogVisible, setPaymentDialogVisible] = useState(false);
  const [orgSettings, setOrgSettings] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const isCancelled = order?.status === 'cancelled';
  const isTransfer = order?.type === 'TRANSFER';
  const [modeConfirmVisible, setModeConfirmVisible] = useState(false);
  const [requiredMode, setRequiredMode] = useState(null);
  const [showBarcodeDialog, setShowBarcodeDialog] = useState(false);

  useEffect(() => {
    if (visible && orderId) {
      setLoading(true);
      axiosInstance
        .get(`/api/inventory/move-order/${orderId}/`)
        .then(res => {
          setOrder(res.data);
          const data = res.data || {};
          // Prefer server-provided aggregate if present, else sum line_total_cost
          if (data.total_purchase_price != null) {
            setOrderTotalCost(Number(data.total_purchase_price));
          } else if (Array.isArray(data.items)) {
            const sum = data.items.reduce((s, it) => s + (Number(it.line_total_cost ?? 0) || 0), 0);
            setOrderTotalCost(sum);
          } else {
            setOrderTotalCost(null);
          }
        })
        .catch(handleApiError)
        .finally(() => setLoading(false));
    } else {
      setOrder(null);
    }
  }, [visible, orderId]);

  useEffect(() => {
    const s = JSON.parse(localStorage.getItem('organizationSettings') || '{}');
    setOrgSettings(s || {});
    const userSettings = JSON.parse(localStorage.getItem('userSettings') || '{}');
      // userSettings may be a simple map; if so, map to the same shape returned by /api/user/me/
      if (userSettings && typeof userSettings === 'object') {
        setCurrentUser({
          username: userSettings.username,
          isAdmin: !!userSettings.isAdmin,
          isApprover: !!userSettings.isApprover,
          isPurchaser: !!userSettings.isPurchaser,
          department: userSettings.department || userSettings.department_id || null,
        });
      } else {
        setCurrentUser(null);
      }
  }, []);

  useEffect(() => {
    // fetch suppliers for receive dialog supplier dropdown
    axiosInstance.get('/api/suppliers/?all=1').then(r => setSuppliers(r.data)).catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    // when order loads, initialize receive items state and reset dialogs
    if (order && Array.isArray(order.items)) {
      setReceiveItemsState(order.items.map(it => {
        const ordered = it.quantity || 0;
        const receivedSoFar = it.received_quantity || 0;
        const remaining = Math.max(0, ordered - receivedSoFar);

        return {
          move_order_item: it.id,
          product_name: it.product_name,
          ordered_quantity: ordered,
          received_so_far: receivedSoFar,
          received_quantity: order.type === 'TRANSFER'
            ? remaining
            : (it.received_quantity ?? ordered ?? 0),
          free_quantity: order.type === 'TRANSFER' ? 0 : (it.free_quantity || 0),
          batch_number: it.batch_number || '',
          expiry_date: ymdToDate(it.expiry_date),
          is_expiring: it.is_expiring || it.product_is_expiring || false,
        };
      }));
      setSelectedInvoiceId(
        (order.supplier_invoices && order.supplier_invoices.length)
          ? order.supplier_invoices[0].id
          : null
      );
      let sId = order.supplier
        || (order.supplier_id)
        || (order.supplier_invoices && order.supplier_invoices.length && order.supplier_invoices[0].supplier)
        || null;
      if (!sId && Array.isArray(order.items) && order.items.length) {
        const firstItem = order.items[0];
        sId = firstItem.supplier || firstItem.supplier_id || null;
      }
      setReceiveSupplierId(sId);
    } else {
      setReceiveItemsState([]);
      setSelectedInvoiceId(null);
    }
  }, [order]);

  const expiryMinDate = useMemo(() => {
    const d = new Date();

    if (!orgSettings) return d;
    const days =
      orgSettings.block_invoice_days_before_expiry ??
      0;

    if (typeof days === 'number' && days > 0) {
      d.setDate(d.getDate() + days);
    }

    return d;
  }, [orgSettings]);

  const refreshOrder = async () => {
    if (!orderId) return;
    setActionLoading(true);
    try {
      const res = await axiosInstance.get(`/api/inventory/move-order/${orderId}/`);
      setOrder(res.data);
    } catch (e) {
      handleApiError(e);
    } finally {
      setActionLoading(false);
    }
  };

  const allowedOrderStatuses = new Set([
    'received',           
    'partially_received', 
    'partially_paid',
  ]);

  const allowedPaymentStatuses = new Set([
    'payment_pending', 
    'partially_paid',
    'partial',         
    'recieved',            
  ]);

  const handlePrintBarcodes = () => {
    setShowBarcodeDialog(true);
  };

  // compute whether Record Payment should be enabled
  const paymentEnabled = !!orderId && !actionLoading && (
    allowedOrderStatuses.has(order?.status) ||
    allowedPaymentStatuses.has(order?.payment_status) ||
    // Some callers may prefer to allow payments when order.receive_on_credit === true AND status indicates received:
    (order?.receive_on_credit && ['received', 'partially_received', 'payment_pending'].includes(order?.status))
  );

  const cancelOrder = async () => {
    if (!orderId || !order) return;
    if (!window.confirm('Are you sure you want to cancel this move order?')) return;

    setActionLoading(true);
    try {
      const res = await axiosInstance.post(`/api/inventory/move-order/${orderId}/cancel/`);
      handleApiSuccess(res.data?.message || 'Move order cancelled');
      onChanged?.();
      await refreshOrder();
    } catch (e) {
      handleApiError(e);
    } finally {
      setActionLoading(false);
    }
  };

  const sendForApproval = async () => {
    if (!orderId) return;
    setActionLoading(true);
    try {
      const res = await axiosInstance.post(`/api/inventory/move-order/${orderId}/send_for_approval/`);
      handleApiSuccess(res.data?.message || 'Sent for approval');
      onChanged?.();
      await refreshOrder();
    } catch (e) { handleApiError(e); } finally { setActionLoading(false); }
  };

  const approveOrder = async (approve = true) => {
    if (!orderId) return;
    setActionLoading(true);
    try {
      if (approve) {
        const res = await axiosInstance.post(`/api/inventory/move-order/${orderId}/approve/`);
        handleApiSuccess(res.data?.message || 'Approved');
        onChanged?.();
      } else {
        // If you implemented a separate reject endpoint, call it; otherwise call a custom endpoint:
        const res = await axiosInstance.post(`/api/inventory/move-order/${orderId}/reject/`, {});
        handleApiSuccess(res.data?.message || 'Rejected');
        onChanged?.();
      }
      await refreshOrder();
    } catch (e) { handleApiError(e); } finally { setActionLoading(false); }
  };

  const openSendDialog = () => {
    const defaultEmail =(headerSupplier && headerSupplier.email) || '';
    setSendEmailTo(defaultEmail);
    setSendDialogVisible(true);
  };

  const doSendToSupplier = async () => {
    if (!orderId) return;
    setActionLoading(true);
    try {
      const res = await axiosInstance.post(`/api/inventory/move-order/${orderId}/send_to_supplier/`, { email_to: sendEmailTo || null });
      handleApiSuccess(res.data?.message || 'Marked as sent to supplier');
      onChanged?.();
      setSendDialogVisible(false);
      await refreshOrder();
    } catch (e) { handleApiError(e); } finally { setActionLoading(false); }
  };

  const handleDispatch = async () => {
      if (order?.status !== 'approved') {
          handleApiError({Error: "Order must be approved to initiate dispatch of goods!"});
          return;
      }
      try {
          const dispatchRes = await axiosInstance.post(`/api/inventory/move-order/${orderId}/dispatch-goods/`);
          handleApiSuccess("Inventory Dispatched!");
          onChanged?.();
          onHide();
      } catch (err) {
          handleApiError(err);
      }
  };

  const openReceiveDialog = () => {
    setReceiveItemsState(prev =>
        (prev || []).map(row => {
          const ordered = row.ordered_quantity || 0;
          const receivedSoFar = row.received_so_far || 0;
          const remaining = Math.max(0, ordered - receivedSoFar);
          return {
            ...row,
            received_quantity: remaining,
            free_quantity: 0,
          };
        })
      );
    if (isTransfer) {
      setReceiveOnCredit(false);   // no credit receive for transfers
    } else {
      setReceiveOnCredit(true);
    }
    setReceiveInvoiceNo('');
    setReceiveDialogVisible(true);
  };

  const doReceiveGoods = async () => {
    if (!orderId) return;
    if (!Array.isArray(receiveItemsState) || receiveItemsState.length === 0) { handleApiError({ Error: 'No items to receive' }); return; }
    // validate
    for (const it of receiveItemsState) {
      if (it.received_quantity == null || Number(it.received_quantity) < 0) {
        handleApiError({ Error: `Invalid received quantity for ${it.product_name}` });
        return;
      }
    }
    if (order?.is_purchase) {
      const missingExpiry = (receiveItemsState || []).filter(
        r => r.is_expiring && !r.expiry_date
      );
      if (missingExpiry.length > 0) {
        handleApiError({
          Error: 'Please enter expiry date for all expiring products before receiving.',
        });
        return;
      }
    }
    // additional validation for credit receives
    if (receiveOnCredit) {
      if (!receiveSupplierId) {
        handleApiError({ Error: 'Supplier is required when receiving on credit' });
        return;
      }
      if (!receiveInvoiceNo || String(receiveInvoiceNo).trim().length === 0) {
        handleApiError({ Error: 'Supplier invoice number is required for credit receipts' });
        return;
      }
    }
    const itemsPayload = receiveItemsState.map(it => ({
      move_order_item: it.move_order_item,
      received_quantity: Number(it.received_quantity || 0),
      free_quantity: Number(it.free_quantity || 0),
      batch_number: it.batch_number || '',
      expiry_date: toYmd(it.expiry_date) || null,
    }));
    setActionLoading(true);
    try {
      const res = await axiosInstance.post(`/api/inventory/move-order/${orderId}/receive/`, {
        items: itemsPayload,
        receive_on_credit: !!receiveOnCredit,
        supplier_invoice_number: receiveInvoiceNo || null,
        supplier: receiveOnCredit ? receiveSupplierId : null,
      });
      handleApiSuccess(res.data?.message || 'Goods received');
      onChanged?.();
      setReceiveDialogVisible(false);
      await refreshOrder();
    } catch (e) { handleApiError(e); } finally { setActionLoading(false); }
  };

  const openPaymentDialog = () => {
    if (currentUser?.isPurchaser || canRecordPayments) {
      setPaymentDialogVisible(true);
    } else {
      handleApiError({Error:"User is not permitted for Payments. Check with your Admin"})
    }
    
  };

  const statusBody = (status) => {
    const severity =
      status === 'Completed' ? 'success' :
      status === 'Cancelled' ? 'danger' : 'info';
    return <Tag value={status} severity={severity} />;
  };

  const getTypeLabel = (type) => {
    if (!type) return '';
    switch (type) {
      case 'DELETE':
        return 'Delete Request';
      case 'TRANSFER':
        return 'Transfer Request';
      case 'NEW':
        return 'New Purchase Request';
      default:
        return type;
    }
  };

  const getTypeSeverity = (type) => {
    switch (type) {
      case 'DELETE':
        return 'danger';
      case 'TRANSFER':
        return 'warning';
      case 'NEW':
        return 'success';
      default:
        return 'info';
    }
  };

  // --- Header supplier info (name + credit limit) ---
  const headerSupplierId = receiveSupplierId ||
    (order && (order.supplier || order.supplier_id)) ||
    (order && order.supplier_invoices && order.supplier_invoices.length && order.supplier_invoices[0].supplier) ||
    null;

  const headerSupplier = Array.isArray(suppliers)
    ? suppliers.find(s => s.id === headerSupplierId)
    : null;

  // Name: prefer supplier object name; fallback to first item's supplier_name
  const headerSupplierName =
    (headerSupplier && headerSupplier.name) ||
    (order && Array.isArray(order.items) && order.items.length && order.items[0].supplier_name) ||
    null;

  const headerSupplierCredit = headerSupplier && headerSupplier.credit_limit != null
    ? Number(headerSupplier.credit_limit)
    : null;

  return (
    <div>
    <Dialog
      header={
          <div>
            <div>
              <span className="text-lg font-semibold">{order?.type} Order Details of: {order?.order_number}</span>
              {(order?.bill_image_urls || []).map((url, idx, all) => (
                <Button key={url} label={all.length > 1 ? `Scanned bill p.${idx + 1}` : 'Scanned bill'} icon="pi pi-image" text size="small" className="ml-2"
                        onClick={() => window.open(url, '_blank', 'noopener')} />
              ))}
            </div>
            <div className="flex flex-wrap align-items-center gap-2 sm:gap-2" style={{ margin: '10px' }}>
              {isCancelled ? (
                // 🔹 Cancelled: only show Re-Initiate (plus the close button)
                <Button
                  label="Re-Initiate"
                  icon="pi pi-refresh"
                  onClick={() => {
                    if (onReinitiate && order) {
                      onReinitiate(order);
                    }
                  }}
                  disabled={!order || !canManageMoveOrders}
                  className="p-mr-2"
                  severity="warning"
                  style={{ borderRadius: '6px', fontSize: '14px' }}
                  size="medium"
                />
              ) : (
                <>
              {/* Enable Send for Approval only when draft */}
              <Button label="Send for Approval" icon="pi pi-send" onClick={sendForApproval} disabled={!orderId || actionLoading || (order?.status !== 'draft') || !canManageMoveOrders} className="p-mr-2" severity="warning" style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" />
              {order?.is_delete === false && (
                <>
                {order?.is_purchase && (
                <Button label="Send to Supplier" icon="pi pi-envelope" onClick={openSendDialog} className="p-mr-2" severity="help" disabled={!orderId || !canManageMoveOrders || actionLoading || !(['approved','draft','pending_approval'].includes(order?.status)) || (orgSettings && orgSettings.require_approval_for_purchases && order?.status !== 'approved')} style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" />
                )}
                {order?.is_purchase === false && (
                <Button label="Dispatch Goods" icon="pi pi-upload" onClick={handleDispatch} className="p-mr-2" severity="info" disabled={!orderId || !canManageMoveOrders || !['approved','partially_received','draft'].includes(order?.status) || !(currentUser && currentUser.department && order?.source_department && currentUser.department === order.source_department)} style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" />
                )}
                <Button label="Receive Goods" icon="pi pi-download" onClick={openReceiveDialog} className="p-mr-2" severity="success" disabled={!orderId || !canManageMoveOrders || actionLoading || !['waiting_to_receive','approved','sent_to_supplier','partially_received','draft'].includes(order?.status) || (order?.status === 'draft' && orgSettings && !orgSettings.allow_direct_grn_auto_approve && orgSettings.require_approval_for_purchases) || !(currentUser && currentUser.department && order?.destination_department && currentUser.department === order.destination_department)} style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" />
                {order?.is_purchase && (
                <Button label="Record Payment" icon="pi pi-money-bill" onClick={openPaymentDialog} disabled={!paymentEnabled || (!currentUser?.isPurchaser && !canRecordPayments)} style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" />
                )}
                </>
              )}
              { (currentUser && currentUser.isApprover) && order?.status === 'pending_approval' && (
                <>
                  <Button label="Approve" icon="pi pi-check" onClick={() => approveOrder(true)} severity='success' className="p-mr-2" style={{ borderRadius: '6px', fontSize: '14px' }} size="medium"/>
                  <Button label="Reject" icon="pi pi-times" onClick={() => approveOrder(false)} severity='danger' className="p-mr-2" style={{ borderRadius: '6px', fontSize: '14px' }} size="medium"/>
                </>
              )}
              <Button label="Drop Order" icon="pi pi-trash" outlined onClick={cancelOrder} className="p-mr-2" severity="danger" style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" disabled={!orderId || actionLoading || ['cancelled','completed'].includes(order?.status) || !canManageMoveOrders} />
            </>
            )}
            {order?.is_purchase && (
            <Button
              label="Print PO"
              icon="pi pi-print"
              onClick={() => printPurchaseOrder({ order, supplier: headerSupplier })}
              className="p-mr-2"
              severity="info"
              outlined
              style={{ borderRadius: '6px', fontSize: '14px' }}
              size="medium"
              disabled={!order}
            />
            )}
            <Button label="Print Barcodes" icon="pi pi-print" className="p-mr-2" severity="secondary" onClick={handlePrintBarcodes} style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" disabled={!orderId || actionLoading || !['recieved','partially_received','partially_paid','paid','completed'].includes(order?.status)} />
            <Button label="Cancel" icon="pi pi-times" onClick={onHide} className="p-mr-2" severity="secondary" outlined style={{ borderRadius: '6px', fontSize: '14px' }} size="medium" />
          </div>
          </div>
        }
      visible={visible}
      onHide={onHide}
      style={{ width: '50vw'}}
      maximized={true}
      draggable={false}
      modal
    >
      {loading ? (
        <div className="flex justify-center items-center py-4">Loading...</div>
      ) : order ? (
        <div>
          {order?.is_purchase ? (
          <div className="flex justify-between items-center mb-2" style={{ margin: '5px' }}>
            <div />
            <div className="flex items-center gap-6 text-sm">
              <div>
                <strong>Supplier:</strong>{' '}
                <span>
                  {headerSupplierName || '—'}
                </span>
              </div>
              <div>
                {headerSupplierCredit != null && (
                  <span>
                    <strong>Credit limit:</strong>{' '}
                    LKR {headerSupplierCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )}
              </div>
              <div>
                <strong>Total:</strong>{' '}
                <span style={{ marginLeft: 6 }}>
                  { (order?.total_purchase_price != null || orderTotalCost != null) ? `LKR ${Number(order?.total_purchase_price ?? orderTotalCost ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—' }
                </span>
              </div>
              <div>
                <strong>Paid:</strong>{' '}
                <span style={{ marginLeft: 6 }}>
                  { order?.total_paid != null ? `LKR ${Number(order.total_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—' }
                </span>
              </div>
              <div>
                <strong>Balance:</strong>{' '}
                <span style={{ marginLeft: 6 }}>
                  { order?.outstanding_balance != null ? `LKR ${Number(order.outstanding_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (orderTotalCost != null ? `LKR ${Math.max(0, Number(orderTotalCost) - Number(order?.total_paid || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—') }
                </span>
              </div>
              <div>
                <strong>Pay Status:</strong>{' '}
                <span style={{ marginLeft: 6 }}>
                  { order?.payment_status_display }
                </span>
              </div>
            </div>
          </div>
          ):('')}
          {/* Send to Supplier dialog */}
          <Dialog header="Send PO to Supplier" visible={sendDialogVisible} onHide={() => setSendDialogVisible(false)} modal style={{ width: 520 }}>
            <div className="p-fluid">
              <div className="field">
                <label>Supplier Email</label>
                <InputText value={sendEmailTo || (headerSupplier && headerSupplier.email) || ''} onChange={(e) => setSendEmailTo(e.target.value)} placeholder="supplier@example.com" />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button label="Cancel" className="p-button-secondary" onClick={() => setSendDialogVisible(false)} outlined />
                <Button label="Send" icon="pi pi-envelope" onClick={doSendToSupplier} loading={actionLoading} />
              </div>
            </div>
          </Dialog>

          {/* Receive Goods dialog */}
          <Dialog header="Receive Goods" visible={receiveDialogVisible} onHide={() => setReceiveDialogVisible(false)} modal style={{ width: '70vw' }}>
            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              <DataTable value={receiveItemsState} responsiveLayout="scroll">
                <Column header="Product" body={(r) => <div>{r.product_name}</div>} />
                <Column header="Ordered" body={(r) => <div>{r.ordered_quantity}</div>} />
                {/* New: show already received quantity */}
                <Column header="Received" body={(r) => <div>{r.received_so_far ?? 0}</div>} />
                {/* Input: remaining/receiving quantity */}
                <Column header="Receiving Qty" body={(r, opts) => (
                  <InputNumber
                    value={r.received_quantity}
                    min={0}
                    onValueChange={(e) => {
                      const nv = [...receiveItemsState];
                      nv[opts.rowIndex] = {
                        ...nv[opts.rowIndex],
                        received_quantity: e.value || 0,
                      };
                      setReceiveItemsState(nv);
                    }}
                  />
                )} />
                <Column header="Free Qty" body={(r, opts) => (
                  <InputNumber
                    value={r.free_quantity || 0}
                    min={0}
                    disabled={isTransfer}  // disabled for transfer orders
                    onValueChange={(e) => {
                      const nv = [...receiveItemsState];
                      nv[opts.rowIndex] = {
                        ...nv[opts.rowIndex],
                        free_quantity: e.value || 0,
                      };
                      setReceiveItemsState(nv);
                    }}
                  />
                )} />
                <Column header="Expiry" body={(r, opts) => (
                  <Calendar
                    value={r.expiry_date}
                    dateFormat="yy-mm-dd"
                    showIcon
                    disabled={isTransfer}  // expiry not editable for transfers
                    onChange={(e) => {
                      const nv = [...receiveItemsState];
                      nv[opts.rowIndex] = {
                        ...nv[opts.rowIndex],
                        expiry_date: e.value,
                      };
                      setReceiveItemsState(nv);
                    }}
                    minDate={expiryMinDate}
                  />
                )} />
              </DataTable>
            </div>
            <div className="p-fluid mt-3">
              {!isTransfer && (
                <>
              <div className="field">
                <Checkbox inputId="oncredit" checked={receiveOnCredit} onChange={(e) => setReceiveOnCredit(e.checked)} />
                <label htmlFor="oncredit" style={{ marginLeft: 8 }}>Receive on credit (create supplier invoice)</label>
              </div>
              {receiveOnCredit && (
                <>
                  <div className="field">
                    <label>Supplier</label>
                    <Dropdown value={receiveSupplierId} options={suppliers} optionLabel="name" optionValue="id"
                              onChange={(e) => setReceiveSupplierId(e.value)} placeholder="Select supplier" disabled/>
                  </div>
                  <div className="field">
                    <label>Supplier invoice number (required)</label>
                    <InputText value={receiveInvoiceNo} onChange={(e) => setReceiveInvoiceNo(e.target.value)} />
                  </div>
                </>
              )}
              </>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button label="Cancel" className="p-button-secondary" onClick={() => setReceiveDialogVisible(false)} outlined />
              <Button label="Receive" icon="pi pi-download" onClick={doReceiveGoods} loading={actionLoading} />
            </div>
          </Dialog>

          {/* Record Payment Dialog */}
              <RecordPaymentDialog
                visible={paymentDialogVisible}
                onHide={() => setPaymentDialogVisible(false)}
                order={order}
                onSuccess={() => {
                  setPaymentDialogVisible(false);
                  refreshOrder();
                  onChanged?.();
                }}
              />
          
          <div className="grid grid-cols-2 gap-4" style={{margin:'5px'}}>
            {order.source_department_name && (
              <>
              <div>
                <strong>From:</strong> {order.source_department_name}
              </div>
              </>
            )}
            {order.destination_department_name && (
              <>
              <div>
                <strong>To:</strong> {order.destination_department_name}
              </div>
              </>
            )}
            <div>
              <strong>Status:</strong> {order?.status_display ? order.status_display : statusBody(order?.status)}
            </div>
            <div>
              <strong>Created By:</strong> {order.created_by_name}
            </div>
            <div>
              <strong>Created At:</strong>{' '}
              {new Date(order.created_at).toLocaleString()}
            </div>
            {order.order_reason && (
              <>
              <div>
                <strong>Reason:</strong> {order.order_reason}
              </div>
              </>
            )}
            {order.reason_remarks && (
              <>
              <div>
                <strong>Remarks:</strong> {order.reason_remarks}
              </div>
              </>
            )}
            {order.approved_at && (
              <>
                <div>
                  <strong>Approved By:</strong> {order.approved_by_name}
                </div>
                <div>
                  <strong>Approved At:</strong>{' '}
                  {new Date(order.approved_at).toLocaleString()}
                </div>
              </>
            )}
          </div>
          <DataTable
            value={order.items}
            header="Move Order Items"
            className="mt-4"
          >
            <Column field="product_name" header="Product" />
            <Column field="quantity" header="Quantity" />
            <Column field="free_quantity" header="Free Qty" />
            <Column field="purchase_price" header="Purchase Price" />
            <Column field="selling_price" header="Selling Price" />
            <Column field="line_total_cost" header="Total Cost" />
            <Column field="reorder_level" header="ROL" />
            <Column field="expiry_date" header="Expiry Date" />
            <Column field="batch_number" header="Batch Number" />
            <Column field="supplier_name" header="Supplier" />
          </DataTable>
        </div>
      ) : (
        <div className="flex justify-center items-center py-4">No data to display</div>
      )}
    </Dialog>
    <PrinterModeConfirmDialog
      visible={modeConfirmVisible}
      printerName="Label Printer"
      requiredLanguage="TSPL"
      onCancel={() => setModeConfirmVisible(false)}
      onConfirm={() => {
        setModeConfirmVisible(false);
        handlePrintBarcodes();
      }}
    />
    <PrintPOBarcodeDialog
      visible={showBarcodeDialog}
      items={(order?.items || []).map(it => ({
        id: it.id,
        product_name: it.product_name,
        batch_number: it.batch_number,
        default_copies: 1
      }))}
      onHide={() => setShowBarcodeDialog(false)}
      onPrint={(itemsWithCopies) => {
        setShowBarcodeDialog(false);
        printPOBarcodes({
          items: itemsWithCopies
        });
      }}
    />
    </div>
  );
}